import React, { ReactNode } from 'react';
import {
    CapacityModel,
    DatasetType,
    FlightDto,
    LatLonPoint,
    ScenarioAttributesDto,
    ScenarioListRequestDto,
    ScenarioTvCaptureDetails,
    ScenarioTvMatchingKind,
    TimePeriod,
} from '../../../model/CORE';
import { ServiceFactory } from '../../../service/ServiceFactory';
import { notification } from 'antd';
import AppContext from '../../../common/context/AppContext';
import './applicableScenariosPanel.less';
import ScenarioMainPanel from './panels/ScenarioMainPanel';
import ScenarioMapPanel from './panels/ScenarioMapPanels';
import ScenarioInformationPanel from './panels/ScenarioInformationPanel';
import { DetailedScenarioSolutionsRequest, DetailedSolution, TVTimePeriodSelection } from '../../../model/SOL49';
import { DEFAULT_MAP_OPTIONS, MapPanelLayerOptions } from '../map/MapPanel';
import {
    AbstractSolverPanel,
    AbstractSolverPanelProps,
    AbstractSolverPanelState,
    solverConnector,
} from '../solver/AbstractSolverPanel';
import memoizeOne from 'memoize-one';

export enum ScenarioDisplayMode {
    PROBLEMATIC_TV,
    POLYGON_SEARCH,
    SIMPLE,
}

interface ApplicableScenariosPanelProps extends AbstractSolverPanelProps {
    /** Props For Selection by Area */
    selectedAreaPolygon?: LatLonPoint[];
    /** Props For Selection by TV */
    selectedScenarioId?: string;
    selectedTvIds?: string[];
    initialTimePeriod: TimePeriod;
}

interface ApplicableScenariosPanelState extends AbstractSolverPanelState {
    scenarios: ScenarioAttributesDto[];
    selectedScenario?: ScenarioAttributesDto;
    simulationTvSelection?: Array<TVTimePeriodSelection>;
    selectedScenarioFlights?: FlightDto[];
    selectedTimePeriod: TimePeriod;
    selectedSimulation?: ScenarioSimulation;
    simulationStatus: Map<string, Array<ScenarioSimulation>>; // scenarioId -> Sim
}

export class ScenarioSimulation {
    scenarioId: string;
    simulationName: string;
    simulationStatus: ScenarioSimulationStatus;
    simulationSolutionIdx?: number; // number corresponds to the solution idx from solverSolutions
    simulationError?: Error;
}

export enum ScenarioSimulationStatus {
    IN_PROGRESS,
    SUCCESS,
    FAILED,
}

export enum PanelDisplayMode {
    SCENARIO,
    SIMULATION,
}

const MAP_LAYER_OPTIONS: MapPanelLayerOptions = {
    measuresLayerEnabled: true,
    tvImpactLayerEnabled: true,
    weatherLayerEnabled: true,
    showNiaImpactedExtra: false,
};

export class ApplicableScenariosPanel extends AbstractSolverPanel<
    ApplicableScenariosPanelProps,
    ApplicableScenariosPanelState
> {
    protected getMapLayerOptions(): MapPanelLayerOptions {
        return MAP_LAYER_OPTIONS;
    }

    public static MAIN_PANEL_WIDTH_EM = 40;
    private static TV_FLIGHT_PANEL_WIDTH_EM = 90;
    private static MAP_PANEL_WIDTH_EM = 80;
    public static INITIAL_WIDTH = ApplicableScenariosPanel.MAIN_PANEL_WIDTH_EM + 'em';
    static MAP_OPTIONS = Object.assign(DEFAULT_MAP_OPTIONS, { centerOnSelectedTvs: true });

    constructor(props: ApplicableScenariosPanelProps) {
        super(props);
        this.state = Object.assign(this.state, {
            scenarios: [],
            loading: true,
            showSeparator: false,
            selectedTimePeriod: this.props.initialTimePeriod,
            simulationStatus: new Map(),
            panelIdxVisible: 0,
        });
    }

    componentDidMount() {
        this.getApplicableScenarios();
        super.componentDidMount();
    }

    componentWillUnmount() {
        super.componentWillUnmount();
    }

    componentDidUpdate(
        prevProps: Readonly<React.PropsWithChildren<ApplicableScenariosPanelProps>>,
        prevState: Readonly<ApplicableScenariosPanelState>,
    ) {
        if (prevState !== this.state) {
            if (
                prevState.selectedTimePeriod &&
                (prevState.selectedTimePeriod.from !== this.state.selectedTimePeriod.from ||
                    prevState.selectedTimePeriod.to !== this.state.selectedTimePeriod.to)
            ) {
                this.getApplicableScenarios();
            }
        }
    }

    private getApplicableScenarios() {
        this.setState({ loading: true });
        const req: ScenarioListRequestDto = {
            dataset: AppContext.getDataset(),
            areaPolygon: this.props.selectedAreaPolygon,
            tvIds: this.props.selectedTvIds,
            timePeriod: this.state.selectedTimePeriod,
            includeCapturedCount: true,
        };
        ServiceFactory.stamCoreService()
            .getScenarioList(req)
            .then(reply => {
                if (reply.error) {
                    throw new Error(reply.error.message);
                }
                this.setState({
                    scenarios: reply.data,
                    loading: false,
                });
                if (this.props.selectedScenarioId) {
                    this.onScenarioSelected(
                        reply.data.find(scenario => scenario.scenarioId === this.props.selectedScenarioId),
                    );
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    }

    /**
     * HANDLERS
     */
    private onScenarioSelected = (scenario: ScenarioAttributesDto) => {
        this.setState(
            {
                selectedScenario: scenario,
                selectedSimulation: null,
                selectedScenarioFlights: [],
            },
            () => this.showSidePanel(3),
        );
    };

    private onScenarioFlightsSelected = (flights: FlightDto[]) => {
        this.setState({
            selectedScenarioFlights: flights,
        });
    };

    private onSimulationSelected = (simulationState: ScenarioSimulation) => {
        if (simulationState && simulationState.simulationSolutionIdx) {
            this.props.setDetailedSolutions([this.state.solverSolutions[simulationState.simulationSolutionIdx]]);
        }
        this.setState({
            selectedSimulation: simulationState ? simulationState : null,
            solverSelectedSolutionIdx: simulationState ? simulationState.simulationSolutionIdx : null,
            solverSidePanelType: simulationState ? 'map' : null,
        });
    };

    private onSelectedTimePeriodChange = (selectedTimePeriod: TimePeriod) => {
        this.setState({
            selectedTimePeriod: selectedTimePeriod,
        });
    };

    private onRequestSimulation = (scenario: ScenarioAttributesDto, simulationName: string) => {
        this.setState(prevState => {
            const newSimulationStatus = new Map(prevState.simulationStatus);
            const simStates = newSimulationStatus.get(scenario.scenarioId) ?? [];

            const updatedSimState = [
                ...simStates,
                {
                    scenarioId: scenario.scenarioId,
                    simulationName: simulationName,
                    simulationStatus: ScenarioSimulationStatus.IN_PROGRESS,
                },
            ];
            newSimulationStatus.set(scenario.scenarioId, updatedSimState);
            return { simulationStatus: newSimulationStatus };
        });
        const request: DetailedScenarioSolutionsRequest = {
            capacityModel: CapacityModel.CCM,
            dataset: { type: DatasetType.OPERATIONAL },
            impactedTvs: this.props.selectedTvIds
                ? this.props.selectedTvIds
                : scenario.scenarioPublishedTrafficVolumeIds,
            includeNiaImpactedExtra: false,
            relativeTv: scenario.scenarioPublishedTrafficVolumeIds[0],
            relativeTvMatchingKind: ScenarioTvMatchingKind.SAME_TRAFFIC_VOLUME,
            scenarioId: scenario.scenarioId,
            timePeriod: this.state.selectedTimePeriod,
        };
        ServiceFactory.sol49Service()
            .getDetailedScenarioSolution(request)
            .then(reply => {
                if (reply.error) {
                    throw new Error(reply.error.message);
                } else if (!reply.data || !reply.data.solutions || reply.data.solutions.length === 0) {
                    throw new Error('No solution found');
                } else if (reply.data.solutions.length > 1) {
                    throw new Error('Unexpected number of solutions found');
                }
                this.updateSimulationState(
                    scenario,
                    simulationName,
                    ScenarioSimulationStatus.SUCCESS,
                    reply.data.solutions[0],
                );
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
                this.updateSimulationState(scenario, simulationName, ScenarioSimulationStatus.FAILED, reason);
            });
    };

    private updateSimulationState(
        scenario: ScenarioAttributesDto,
        simulationName: string,
        simulationStatus: ScenarioSimulationStatus,
        solutionResult?: DetailedSolution | Error,
    ) {
        this.setState(prevState => {
            const newSimulationStatus = new Map(prevState.simulationStatus);
            const simStates: ScenarioSimulation[] = newSimulationStatus.get(scenario.scenarioId) ?? [];
            const newSolverSolutions = prevState.solverSolutions ? [...prevState.solverSolutions] : [];
            if (!(solutionResult instanceof Error)) {
                newSolverSolutions.push(solutionResult);
            }
            const scenarioSimulation: ScenarioSimulation = {
                scenarioId: scenario.scenarioId,
                simulationName: simulationName,
                simulationStatus: simulationStatus,
                simulationSolutionIdx:
                    solutionResult && !(solutionResult instanceof Error) ? newSolverSolutions.length - 1 : undefined,
                simulationError: solutionResult && solutionResult instanceof Error ? solutionResult : undefined,
            };
            const updatedSimState: ScenarioSimulation[] = [
                ...simStates.filter(simState => simState.simulationName !== simulationName),
                scenarioSimulation,
            ];
            newSimulationStatus.set(scenario.scenarioId, updatedSimState);
            if (
                this.state.selectedSimulation &&
                this.state.selectedSimulation.scenarioId === scenarioSimulation.scenarioId &&
                this.state.selectedSimulation.simulationName === scenarioSimulation.simulationName
            ) {
                this.props.setDetailedSolutions([newSolverSolutions[newSolverSolutions.length - 1]]);
                return {
                    selectedSimulation: scenarioSimulation,
                    solverSolutions: newSolverSolutions,
                    simulationStatus: newSimulationStatus,
                    solverSelectedSolutionIdx: newSolverSolutions.length - 1,
                };
            } else {
                return {
                    solverSolutions: newSolverSolutions,
                    simulationStatus: newSimulationStatus,
                    solverSelectedSolutionIdx: prevState.solverSelectedSolutionIdx,
                };
            }
        });
    }

    /**
     * Overridden to never hide third panel
     */
    handleShowOnMap = () => {
        if (this.state.solverSidePanelType !== 'map') {
            this.setState({ solverSidePanelType: 'map' }, () => this.showSidePanel(3));
        }
    };
    handleShowTVsImpact = () => {
        if (this.state.solverSidePanelType !== 'tvs') {
            this.setState({ solverSidePanelType: 'tvs' }, () => this.showSidePanel(3));
        }
    };
    handleShowMeasuresImpact = () => {
        if (this.state.solverSidePanelType !== 'measures') {
            this.setState({ solverSidePanelType: 'measures' }, () => this.showSidePanel(3));
        }
    };

    /**
     * PANELS Memoize used since getMainPanel functions called many times with the same state see extensiblePanels implementation
     */
    getPanels(): Map<React.ReactNode, number> {
        const panels = new Map<ReactNode, number>();
        panels.set(this.getMainPanel(this.state), ApplicableScenariosPanel.MAIN_PANEL_WIDTH_EM);
        panels.set(this.getSecondaryPanel(this.state), ApplicableScenariosPanel.TV_FLIGHT_PANEL_WIDTH_EM);
        panels.set(this.getExtraPanel(this.state), ApplicableScenariosPanel.MAP_PANEL_WIDTH_EM);
        return panels;
    }

    private getMainPanel = memoizeOne(state => {
        return (
            <ScenarioMainPanel
                displayMode={
                    this.props.selectedTvIds
                        ? ScenarioDisplayMode.PROBLEMATIC_TV
                        : this.props.selectedAreaPolygon
                          ? ScenarioDisplayMode.POLYGON_SEARCH
                          : ScenarioDisplayMode.SIMPLE
                }
                scenarios={this.state.scenarios}
                selectedTvIds={this.props.selectedTvIds}
                onSelectScenario={this.onScenarioSelected}
                selectedTimePeriod={this.state.selectedTimePeriod}
                selectedScenario={this.state.selectedScenario}
                onSelectedTimePeriodChange={this.onSelectedTimePeriodChange}
            />
        );
    });

    private getSecondaryPanel = memoizeOne(state => {
        return (
            this.state.selectedScenario && (
                <ScenarioInformationPanel
                    selectedScenario={this.state.selectedScenario}
                    selectedTimePeriod={this.state.selectedTimePeriod}
                    onRequestSimulation={this.onRequestSimulation}
                    simulations={this.state.simulationStatus.get(this.state.selectedScenario.scenarioId)}
                    onSelectSimulation={this.onSimulationSelected}
                    selectedSimulation={this.state.selectedSimulation}
                    solverSolutions={this.state.solverSolutions}
                    solverOnShowOnMap={this.handleShowOnMap}
                    solverOnRrtFlightShowOnMap={this.handleRrtFlightShowOnMap}
                    solverOnShowTVsImpact={this.handleShowTVsImpact}
                    solverOnShowMeasuresImpact={this.handleShowMeasuresImpact}
                    onSelectFlights={this.onScenarioFlightsSelected}
                />
            )
        );
    });

    private getExtraPanel = memoizeOne(state => {
        if (this.state.selectedSimulation) {
            return this.getSolverExtraPanelContent();
        } else {
            return (
                this.state.selectedScenario && (
                    <ScenarioMapPanel
                        selectedTimePeriod={this.state.selectedTimePeriod}
                        selectedScenario={this.state.selectedScenario}
                        selectedFlights={this.state.selectedScenarioFlights}
                    />
                )
            );
        }
    });
}

export default solverConnector(ApplicableScenariosPanel);
